import { useCallback, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/material/Autocomplete";
import PerfumeComposer from "@/components/PerfumeComposer";
import { services } from "@/injectServices";
import { PageLink, PerfumeTunnelPageStepId, type Perfume } from "@/models";
import { usePerfumeEditor } from "@/services";
import { useTrackIngredientLots } from "@/hooks/useIngredientLots";
import { PerfumeDetails } from "./PerfumeDetails";
import { LockPerfumeWithCode } from "./LockPerfumeWithCode";
import { SendPerfumeByEmail } from "./SendPerfumeByEmail";
import { setPerfumeProperty } from "@/services/perfume-editor";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import InfoIcon from "@mui/icons-material/Info";

interface PageStep {
  step: PerfumeTunnelPageStepId;
  title: string;
  nextButton: string | null;
}

export function PerfumeTunnel({
  backendPerfume,
  activePageNumber,
  registerLockPerfume,
  onEditStarted,
}: {
  backendPerfume: Perfume;
  activePageNumber: PerfumeTunnelPageStepId;
  // gives the parent a way to save and lock the perfume being edited
  registerLockPerfume: (lockPerfume: (() => Promise<void>) | null) => void;
  // the client changed the perfume (the change may not be saved yet)
  onEditStarted: () => void;
}) {
  const { t } = useTranslation();
  const ingredientLots = useTrackIngredientLots(services().repositories.ingredientLotsRepository);
  const editor = usePerfumeEditor({
    service: services().perfumeService,
    backendPerfume,
    ingredientLots,
    onEdit: onEditStarted,
  });

  const { lockPerfume } = editor;
  useEffect(() => {
    registerLockPerfume(lockPerfume);
    return () => registerLockPerfume(null);
  }, [registerLockPerfume, lockPerfume]);

  const displayedPages: ReadonlyArray<PageStep> = useMemo(
    () =>
      [
        {
          step: PerfumeTunnelPageStepId.Identification,
          title: t("identificationStep"),
          nextButton: t("navNext"),
        },
        {
          step: PerfumeTunnelPageStepId.Parfum,
          title: `${t("perfumeStep")} ${backendPerfume.formulaNumber}`,
          nextButton: t("navLastStep"),
        },
        {
          step: PerfumeTunnelPageStepId.Diploma,
          title: t("diplomaStep"),
          nextButton: null,
        },
      ] as const,
    [t, backendPerfume.formulaNumber],
  );

  const previousPage = displayedPages[activePageNumber - 1];
  const activePage = displayedPages[activePageNumber];
  const nextPage = displayedPages[activePageNumber + 1];

  const navigate = useNavigate();

  const navigateToStep = useCallback(
    (toStep: PerfumeTunnelPageStepId) => {
      navigate(
        PageLink.perfumeWorkshop({
          perfumeId: backendPerfume.recipeId,
          step: toStep,
        }),
        // replace: a perfume must leave a single entry in the browser history of shared devices
        { replace: true },
      );
    },
    [backendPerfume.recipeId, navigate],
  );

  const theme = useTheme();
  const stepperOrientation = useMediaQuery(theme.breakpoints.down("sm"))
    ? "vertical"
    : "horizontal";

  const proposedTitles = [t("title_01"), t("title_02"), t("title_03"), t("title_04")].filter(
    (title) => title.length,
  );

  return (
    <>
      <Stepper activeStep={activePageNumber} sx={{ pt: 3, pb: 5 }} orientation={stepperOrientation}>
        {displayedPages.map((page) => (
          <Step key={page.step}>
            <StepLabel>{page.title}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <>
        <Box
          sx={{
            display:
              activePage.step !== PerfumeTunnelPageStepId.Identification ? "none" : undefined,
          }}
        >
          {t("identificationStep")}
          <FormControl
            fullWidth
            sx={{
              my: 2,
            }}
          >
            <TextField
              required
              label="Email"
              value={editor.perfumeViewModel.email}
              onChange={(e) =>
                setPerfumeProperty(editor, {
                  newEmail: e.target.value,
                })
              }
              variant="standard"
            />
          </FormControl>
          <FormControl
            fullWidth
            sx={{
              my: 2,
            }}
          >
            <Autocomplete
              freeSolo
              options={proposedTitles}
              value={editor.perfumeViewModel.title}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t("title")}
                  onChange={(e) =>
                    setPerfumeProperty(editor, {
                      newTitle: e.target.value,
                    })
                  }
                  variant="standard"
                />
              )}
            />
          </FormControl>
          <FormControl
            fullWidth
            sx={{
              my: 2,
            }}
          >
            <TextField
              required
              label={t("firstName")}
              value={editor.perfumeViewModel.username}
              onChange={(e) =>
                setPerfumeProperty(editor, {
                  newUsername: e.target.value,
                })
              }
              variant="standard"
            />
          </FormControl>
          <FormControl
            fullWidth
            sx={{
              my: 2,
            }}
          >
            <TextField
              required
              label={t("lastName")}
              value={editor.perfumeViewModel.userLastName}
              onChange={(e) =>
                setPerfumeProperty(editor, {
                  newUserLastName: e.target.value,
                })
              }
              variant="standard"
            />
          </FormControl>
          <Alert
            sx={{ marginTop: "12px" }}
            icon={<InfoIcon fontSize="inherit" />}
            severity="success"
          >
            {t("tabletMessage")}
          </Alert>
          <Alert sx={{ marginTop: "12px" }} icon={<InfoIcon fontSize="inherit" />} severity="info">
            {t("tipMessage")}
          </Alert>
        </Box>

        <PerfumeComposer
          editor={editor}
          hide={activePage.step !== PerfumeTunnelPageStepId.Parfum}
        />
      </>

      {activePage.step === PerfumeTunnelPageStepId.Diploma && (
        <Stack sx={{ alignItems: "center" }}>
          <PerfumeDetails perfume={backendPerfume} />
          <SendPerfumeByEmail editor={editor} recipeId={backendPerfume.recipeId} />
          {/* an admin still edits a locked perfume in the tunnel */}
          {backendPerfume.lockedAt === null && <LockPerfumeWithCode onLock={lockPerfume} />}
        </Stack>
      )}

      {editor.saveError && (
        <Alert sx={{ marginTop: "12px" }} severity="error">
          {t("saveError")}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {previousPage && (
          <Button onClick={() => navigateToStep(previousPage.step)} sx={{ mt: 3, ml: 1 }}>
            {t("navBack")}
          </Button>
        )}

        {nextPage && (
          <Button
            variant="contained"
            onClick={() => navigateToStep(nextPage.step)}
            sx={{ mt: 3, ml: 1 }}
          >
            {activePage.nextButton}
          </Button>
        )}
      </Box>
    </>
  );
}
